import React, { useState } from "react";
import {
  Box,
  Snackbar,
  Typography,
  Card,
  useTheme,
  Container,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import BrokerTable from "./BrokerTable";
import BrokerDialog from "./BrokerDialog";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { selectBrokers } from "../../Redux/Reducers/selectors";

const BrokerPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const brokers = useSelector(selectBrokers);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBroker, setEditBroker] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const handleAdd = () => {
    setEditBroker(null);
    setDialogOpen(true);
  };

  const handleEdit = (broker) => {
    setEditBroker(broker);
    setDialogOpen(true);
  };

  const handleDelete = (broker) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete broker "${broker.name}" (${broker.abbreviation})? This action cannot be undone.`
    );

    if (confirmDelete) {
      sendWebSocketMessage(
        `{"action":"delete_broker", "data":{"broker":${JSON.stringify(
          broker
        )}}}`
      );
      dispatch(addActiveRequests("delete_broker"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Deleting Broker..."));
      dispatch(addActiveRequests("delete_broker"));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditBroker(null);
  };

  const handleSave = (broker) => {
    if (!broker || Object.keys(broker).length === 0) return;

    if (editBroker && editBroker.brokerId) {
      sendWebSocketMessage(
        `{"action":"modify_broker", "data":{"broker":${JSON.stringify({
          ...broker,
          brokerId: editBroker.brokerId,
          date: editBroker.date,
          routers: editBroker.routers,
        })}}}`
      );
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Updating Broker..."));
      dispatch(addActiveRequests("modify_broker"));
    } else {
      broker.abbreviation = broker.abbreviation.toLowerCase();
      if (brokers.find((a) => a.abbreviation === broker.abbreviation)) {
        alert(`${broker.abbreviation} already exists, try new one.`);
        return;
      }

      sendWebSocketMessage(
        `{"action":"new_broker", "data":${JSON.stringify({
          ...broker,
        })}}`
      );
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Adding Broker..."));
      dispatch(addActiveRequests("new_broker"));
    }
    setDialogOpen(false);
    setEditBroker(null);
  };

  return (
    <>
      <Helmet>
        <title>Brokers | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode === "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            Brokers
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Manage broker relationships and trading configurations
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <BrokerTable
            brokers={brokers}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAdd={handleAdd}
          />
        </Card>
      </Container>

      <BrokerDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        broker={editBroker}
        onSave={handleSave}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
    </>
  );
};

export default BrokerPage;
